'use client';

import { getCookie } from '@/app/actions/cookies';
import {
  bindWechatByWxOauthCode,
  getFullUserInfo,
  loginByWxOAuthCode,
  User,
} from '@/app/actions/user';
import ActivateCardModal from '@/components/activate-card-modal';
import ModalDirective from '@/components/modal-directive';
import { clearCachedToken } from '@/lib/client-request';
import { siteConfig } from '@/config/site';
import { isMobileDevice } from '@/lib/device-utils';
import { reloadOnceForServerActionVersionSkew } from '@/lib/server-action-version-skew';
import { retrieveWxStateInfo } from '@/lib/wx-oauth';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

export const AuthContext = createContext<{
  user: User | null;
  loading: boolean;
  updateUserInfo: () => Promise<void>;
  triggerLogin?: (callback?: (user?: User) => void) => void;
  showActivateCardModal: () => void;
}>({
  user: null,
  loading: false,
  updateUserInfo: async () => {},
  triggerLogin: undefined,
  showActivateCardModal: () => {},
});

export const useAuth = () => {
  return useContext(AuthContext);
};

/**
 * 处理微信 OAuth 回调和 referralCode 的子组件。
 * 使用 useSearchParams，必须在 Suspense 内渲染以避免整页 CSR bail out。
 */
function AuthCallbackHandler({
  getUser,
}: {
  getUser: () => Promise<void>;
}) {
  const searchParams = useSearchParams();
  const handledCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const handleCallback = async (): Promise<void> => {
      const referralCode = searchParams.get('referralCode');
      const code = searchParams.get('code');
      const token = await getCookie(siteConfig.tokenName);

      if (referralCode) {
        localStorage.setItem('SUPPR_REFERRAL_CODE', referralCode);
      }

      // 处理微信回调（同一个 code 只处理一次）
      if (!code || handledCodeRef.current === code) return;

      const state = searchParams.get('state') || '';
      const stateInfo = retrieveWxStateInfo(state);
      if (!stateInfo) {
        // 可能是无关的 code 参数（比如外部链接），不属于当前会话发起的 OAuth，忽略
        return;
      }
      handledCodeRef.current = code;

      if (stateInfo.action === 'bind_wechat') {
        // 绑定场景：必须已登录
        if (!token) {
          toast.error('请先登录后再绑定微信', {
            position: 'top-center',
          });
          return;
        }
        try {
          await bindWechatByWxOauthCode(code);
          toast.success('微信绑定成功', { position: 'top-center' });
          await getUser();
        } catch (e: unknown) {
          if (reloadOnceForServerActionVersionSkew(e)) return;
          toast.error(e instanceof Error ? e.message : '绑定微信失败', {
            position: 'top-center',
          });
        }
        return;
      }

      // 登录场景：仅在未登录时处理
      if (token) return;
      const source = searchParams.get('source');
      loginByWxOAuthCode(code, referralCode, source).then(() => {
        clearCachedToken();
        getUser();
      });
    };

    handleCallback();
  }, [searchParams]);

  return null;
}

export const AuthProvider = ({ children }: { children?: ReactNode }) => {
  const router = useRouter();
  const pendingCallbackRef = useRef<((user?: User) => void) | null>(null);

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const activateReminderShownRef = useRef(false);

  const updateUserInfo = useCallback(async () => {
    try {
      const {
        user: newUser,
        pointInfo,
        apiPointInfo,
      } = await getFullUserInfo();
      if (newUser) {
        if (pointInfo) newUser.pointInfo = pointInfo;
        if (apiPointInfo) newUser.apiPointInfo = apiPointInfo;
        newUser.got_free_week_membership &&
          ModalDirective.open({
            title: '恭喜🎉',
            content: '您被系统随机抽中！已经为您赠送高级版会员 7 天。',
            onOk: async () => {
              router.push('/profile');
            },
            okText: '查看详情',
          });
      }
      setUser(newUser);
    } catch (e: unknown) {
      if (reloadOnceForServerActionVersionSkew(e)) return;
      console.error('[AuthProvider] updateUserInfo failed:', e);
    }
  }, [router]);

  const getUser = useCallback(async () => {
    try {
      await updateUserInfo();
    } catch (e: unknown) {
      if (reloadOnceForServerActionVersionSkew(e)) return;
      toast.error('请求错误', {
        position: 'top-center',
        description: e instanceof Error ? e.message : '未知错误',
      });
    } finally {
      setLoading(false);
    }
  }, [updateUserInfo]);

  const triggerLogin = (callback?: (user?: User) => void) => {
    const referralCode = localStorage.getItem('SUPPR_REFERRAL_CODE');

    if (callback) {
      pendingCallbackRef.current = callback;
    }

    const params = new URLSearchParams();
    if (referralCode) params.set('referralCode', referralCode);
    // 保留回跳地址，登录成功后回到原页面（而不是统一跳 /）
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== '/login') {
        params.set('callback', currentPath);
      }
    }
    const url = `/login${params.size ? '?' + params.toString() : ''}`;

    // 移动端（包括微信内置浏览器）使用硬跳转，避免 Next.js 拦截路由把 /login 变成
    // 浮层弹窗——浮层情况下微信 OAuth 跳转回来时地址栏是 /login 但底层还是原页面，
    // 会出现"闪一下又跳回"的问题。
    if (typeof window !== 'undefined' && isMobileDevice()) {
      window.location.href = url;
      return;
    }
    router.push(url);
  };

  const showActivateCardModal = () => {
    setActivateModalOpen(true);
  };

  // 全局激活提醒：非会员 + 有 QUEUED 卡 + 免费试用已过期
  useEffect(() => {
    if (!user || activateReminderShownRef.current) return;
    const pointInfo = user.pointInfo;
    if (
      pointInfo?.can_activate_card &&
      pointInfo?.queued_cards &&
      pointInfo.queued_cards.length > 0
    ) {
      activateReminderShownRef.current = true;
      setActivateModalOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (user && pendingCallbackRef.current) {
      const cb = pendingCallbackRef.current;
      pendingCallbackRef.current = null;
      cb(user);
    }
  }, [user]);

  useEffect(() => {
    const t0 = Date.now();
    getUser().then(() => {
      console.log(`[AuthProvider] getUser completed in ${Date.now() - t0}ms`);
    });
  }, []);

  const queuedCards =
    user?.pointInfo?.queued_cards?.filter((c) => c.card_status === 'QUEUED') ??
    [];

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        updateUserInfo,
        triggerLogin,
        showActivateCardModal,
      }}
    >
      {children}
      <Suspense>
        <AuthCallbackHandler getUser={getUser} />
      </Suspense>
      <ActivateCardModal
        cards={queuedCards}
        isOpen={activateModalOpen}
        onOpenChange={setActivateModalOpen}
        onSuccess={() => {
          updateUserInfo();
        }}
      />
    </AuthContext.Provider>
  );
};
